import React, { useState } from "react";
import axios from "axios";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import "./AddGuppyScreen.css"; // Reuse the same styling
import AdminAppBar from "./AdminAppBar";

const AddPlantScreen = () => {
  const [newPlant, setNewPlant] = useState({
    name: "",
    price: "",
    quantity: "",
    tags: "",
    type: "",
    care: "",
    sold_status: false, // Changed default to false (not sold)
    image: null,
  });
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 50000000) {
        setError("File size should not exceed 50MB");
      } else if (!file.type.startsWith("image/")) {
        setError("Only image files are allowed.");
      } else {
        setError("");
        setNewPlant((prev) => ({ ...prev, image: file }));
      }
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewPlant((prev) => ({ ...prev, [name]: value }));
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);
    const uploadUrl = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;

    try {
      const response = await axios.post(uploadUrl, formData, {
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        },
      });
      return response.data.secure_url;
    } catch (error) {
      console.error("Upload Error:", error);
      alert("Failed to upload image.");
      return null;
    }
  };

  const handleUpload = async () => {
    if (error) return alert(error);
    
    // Validation
    if (!newPlant.name.trim()) {
      return alert("Plant name is required");
    }
    if (!newPlant.price || parseFloat(newPlant.price) < 0) {
      return alert("Valid price is required");
    }
    if (!newPlant.quantity || parseInt(newPlant.quantity) < 0) {
      return alert("Valid quantity is required");
    }

    setUploadProgress(0);
    try {
      let imageUrl = null;
      if (newPlant.image) {
        imageUrl = await uploadToCloudinary(newPlant.image);
      }

      const plantData = {
        name: newPlant.name,
        price: parseFloat(newPlant.price || 0),
        quantity: parseInt(newPlant.quantity || 0, 10),
        tags: parseInt(newPlant.tags || 0, 10),
        type: newPlant.type,
        care: newPlant.care,
        sold_status: newPlant.sold_status,
        image: imageUrl,
      };

      const plantCollection = collection(db, "plants");
      await addDoc(plantCollection, plantData);

      try {
        const webhookUrl = process.env.REACT_APP_N8N_ADD_PRODUCT_WEBHOOK_URL || process.env.REACT_APP_N8N_ADD_GUPPY_WEBHOOK_URL;
        if (webhookUrl) {
          const response = await fetch(webhookUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ ...plantData, itemType: "plant" }),
          });

          if (!response.ok) {
            console.error("n8n Webhook Error. Status:", response.status);
            alert("✅ تم حفظ النبات في المتجر\n❌ تأكد من تفعيل Workflow في n8n، لم يتم النشر على فيسبوك!");
          } else {
            console.log("Plant info sent to n8n webhook successfully");
            alert("✅ تم إضافة النبات بنجاح!\n✅ تم نشره على صفحة فيسبوك تلقائياً!");
          }
        } else {
          alert("✅ تم حفظ النبات في المتجر");
        }
      } catch (webhookError) {
        console.error("Failed to send plant info to n8n webhook:", webhookError);
        alert("✅ تم حفظ النبات في المتجر\n❌ حدث خطأ في الاتصال بسيرفر n8n للمنشورات!");
      }
      
      // Reset form
      setNewPlant({
        name: "",
        price: "",
        quantity: "",
        tags: "",
        type: "",
        care: "",
        sold_status: false,
        image: null,
      });
      setUploadProgress(0);
      
      // Reset file input by changing the key
      setFileInputKey(Date.now());
      
    } catch (err) {
      console.error("Upload Error:", err);
      alert("Failed to upload plant.");
    }
  };

  return (
    <div>
      <AdminAppBar />
      <div className="page-container">
        <h1 className="title">Upload Plant Info</h1>
        {error && <p className="error-message">{error}</p>}
        <div className="input-card">
          <label className="label">صورة النبات</label>
          <input key={fileInputKey} type="file" accept="image/*" onChange={handleFileChange} className="input-field" />

          <label className="label">اسم النبات (Name)</label>
          <input
            type="text"
            name="name"
            value={newPlant.name}
            onChange={handleInputChange}
            className="input-field"
            placeholder="Enter plant name"
          />

          <label className="label">السعر (Price)</label>
          <input
            type="number"
            name="price"
            value={newPlant.price}
            onChange={handleInputChange}
            className="input-field"
            placeholder="Enter price in DA"
            min="0"
            step="0.01"
          />

          <label className="label">الكمية المتاحة (Available Quantity)</label>
          <input
            type="number"
            name="quantity"
            value={newPlant.quantity}
            onChange={handleInputChange}
            className="input-field"
            placeholder="Enter available quantity"
            min="0"
          />

          <label className="label">عدد الفروع (Tags/Branches)</label>
          <input
            type="number"
            name="tags"
            value={newPlant.tags}
            onChange={handleInputChange}
            className="input-field"
            placeholder="Enter number of branches"
            min="0"
          />

          <label className="label">نوع النبات (Plant Type)</label>
          <input
            type="text"
            name="type"
            value={newPlant.type}
            onChange={handleInputChange}
            className="input-field"
            placeholder="e.g., نبات داخلي، نبات خارجي"
          />

          <label className="label">مستوى العناية (Care Level)</label>
          <select
            name="care"
            value={newPlant.care}
            onChange={handleInputChange}
            className="input-field"
          >
            <option value="">اختر مستوى العناية</option>
            <option value="سهل">سهل (Easy)</option>
            <option value="متوسط">متوسط (Medium)</option>
            <option value="صعب">صعب (Hard)</option>
          </select>

          <div className="checkbox-container">
            <label className="label">حالة البيع (Sold Status)</label>
            <input
              type="checkbox"
              checked={newPlant.sold_status}
              onChange={(e) => setNewPlant({ ...newPlant, sold_status: e.target.checked })}
              className="checkbox-field"
            />
            <span>{newPlant.sold_status ? "تم البيع (Sold)" : "متاح (Available)"}</span>
          </div>

          <button onClick={handleUpload} className="upload-button">
            Upload Plant
          </button>
          
          {uploadProgress > 0 && (
            <div className="progress-container">
              <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
              <span className="progress-text">{uploadProgress}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddPlantScreen;